import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Filter, UserSearch, IndianRupee, Activity, Search, Save,
  TrendingUp, UserMinus, Gift, Heart, Users2, Calendar, Send, ChevronLeft, ChevronRight, Wallet, AlertCircle, Loader2,
  Building2
} from 'lucide-react';
import styles from './Audience.module.css';
import { useStore } from '../../../../context/StoreContext';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const CUSTOMER_API = `${API_ORIGIN}/api/v1/smiify/sm/customers`;

const Audience = () => {
  const navigate = useNavigate();
  const { activeStore } = useStore();
  const fetchId = activeStore?._id || activeStore?.id;

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [segmentName, setSegmentName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    gender: 'All',
    sortHighSpend: false,      
    sortFrequentVisits: false, 
    filterLosingAudience: false, 
    hasDueAmount: false,       
    hasWalletBalance: false,   
    birthdayMonth: 'All',
    anniversaryMonth: 'All'
  });

  useEffect(() => {
    if (!fetchId || fetchId === 'global_hq') {
        setCustomers([]);
        setIsLoading(false);
        return;
    }

    const fetchAudience = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('smiify_token');
        const res = await fetch(CUSTOMER_API, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'x-store-id': fetchId
          }
        });
        const json = await res.json();
        if (json.success) {
          setCustomers(json.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch audience:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudience();
  }, [fetchId]);

  const handleFilterChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFilters({ ...filters, [e.target.name]: value });
    setCurrentPage(1);
  };

  const toggleSmartFilter = (filterName) => {
    setFilters(prev => {
      let updates = { [filterName]: !prev[filterName] };
      if (filterName === 'sortHighSpend' && updates.sortHighSpend) updates.sortFrequentVisits = false;
      if (filterName === 'sortFrequentVisits' && updates.sortFrequentVisits) updates.sortHighSpend = false;
      return { ...prev, ...updates };
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      gender: 'All', sortHighSpend: false, sortFrequentVisits: false, filterLosingAudience: false,
      hasDueAmount: false, hasWalletBalance: false, birthdayMonth: 'All', anniversaryMonth: 'All'
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const getMonthName = (dateString) => {
    if (!dateString) return 'None';
    return new Date(dateString).toLocaleString('en-US', { month: 'long' });
  };

  const filteredCustomers = useMemo(() => {
    let result = customers.filter(c => {
      if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase()) && !c.phone.includes(searchQuery)) return false;
      if (filters.gender !== 'All' && c.gender !== filters.gender) return false;
      if (filters.hasDueAmount && c.dueAmount <= 0) return false;
      if (filters.hasWalletBalance && c.walletBalance <= 0) return false;

      let lastVisitDays = 999;
      if (c.lastVisit) {
          const diff = new Date() - new Date(c.lastVisit);
          lastVisitDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      }
      if (filters.filterLosingAudience && lastVisitDays <= 60) return false;

      const bMonth = getMonthName(c.dob);
      if (filters.birthdayMonth !== 'All' && bMonth !== filters.birthdayMonth) return false;

      const aMonth = getMonthName(c.anniversary);
      if (filters.anniversaryMonth !== 'All' && aMonth !== filters.anniversaryMonth) return false;

      return true;
    });

    if (filters.sortHighSpend) result.sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0));
    else if (filters.sortFrequentVisits) result.sort((a, b) => (b.totalVisits || 0) - (a.totalVisits || 0));
    
    return result;
  }, [filters, searchQuery, customers]);

  const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);
  const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(filteredCustomers.map(c => c.id));
    else setSelectedIds([]);
  };

  const handleSelectRow = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const totalAudienceSpend = filteredCustomers.reduce((sum, c) => sum + (c.totalSpent || 0), 0);

  // 🚀 ACTION: SAVE SEGMENT TO LOCAL STORAGE
  const handleSaveSegment = () => {
    const finalIds = selectedIds.length > 0 ? selectedIds : filteredCustomers.map(c => c.id);
    const newSegment = {
        id: `seg_${Date.now()}`,
        name: segmentName,
        count: finalIds.length,
        userIds: finalIds
    };

    const existingSegments = JSON.parse(localStorage.getItem('smiify_saved_segments') || '[]');
    localStorage.setItem('smiify_saved_segments', JSON.stringify([...existingSegments, newSegment]));
    
    alert('Segment Saved Successfully! You can now use it in campaigns.');
    setSegmentName('');
  };

  // 🚀 ACTION: USE USERS FOR CAMPAIGN
  const handleUseUsers = () => {
    const finalIds = selectedIds.length > 0 ? selectedIds : filteredCustomers.map(c => c.id);
    const tempSegment = {
        id: `temp_${Date.now()}`,
        name: 'Custom Filtered Selection',
        count: finalIds.length,
        userIds: finalIds
    };
    
    // Pass temp segment to Campaign page via router state
    navigate('/marketing/campaigns/create', { state: { prefilledSegment: tempSegment } });
  };

  if (!fetchId || fetchId === 'global_hq') {
      return (
          <div className={styles.pageWrapper} style={{justifyContent: 'center', alignItems: 'center', height: '80vh'}}>
              <div style={{textAlign: 'center', color: '#64748b'}}>
                  <Building2 size={48} style={{marginBottom: '16px', opacity: 0.5}} />
                  <h3>Please select a branch</h3>
                  <p>Audience builder requires a specific branch to be selected.</p>
              </div>
          </div>
      );
  }

  return (
    <div className={styles.pageWrapper}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h2><UserSearch size={16} color="var(--p-600)" strokeWidth={2.5}/> Audience Builder</h2>
          <p>Live Database. Select users to launch a campaign.</p>
        </div>
        <div className={styles.headerActions}>
          <input 
            type="text" 
            placeholder="Name your segment..." 
            className={styles.segmentNameInput}
            value={segmentName}
            onChange={(e) => setSegmentName(e.target.value)}
          />
          <button className={styles.btnOutline} disabled={!segmentName || filteredCustomers.length === 0} onClick={handleSaveSegment}>
            <Save size={12} /> Save Segment
          </button>
          <button 
            className={styles.btnPrimary} 
            disabled={selectedIds.length === 0 && filteredCustomers.length === 0}
            onClick={handleUseUsers}
          >
            <Send size={12} /> 
            Use {selectedIds.length > 0 ? selectedIds.length : filteredCustomers.length} Users
          </button>
        </div>
      </header>

      <div className={styles.mainContent}>
        <aside className={styles.filterSidebar}>
          <div className={styles.filterHeader}>
            <h3><Filter size={14} /> Quick Filters</h3>
            <button className={styles.clearBtn} onClick={clearFilters}>Clear All</button>
          </div>

          <div className={styles.filterSection}>
            <div className={styles.filterSectionTitle}><Users size={12}/> Gender</div>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}><input type="radio" name="gender" value="All" checked={filters.gender === 'All'} onChange={handleFilterChange} /> All</label>
              <label className={styles.radioLabel}><input type="radio" name="gender" value="Male" checked={filters.gender === 'Male'} onChange={handleFilterChange} /> Male</label>
              <label className={styles.radioLabel}><input type="radio" name="gender" value="Female" checked={filters.gender === 'Female'} onChange={handleFilterChange} /> Female</label>
            </div>
          </div>

          <div className={styles.filterSection}>
            <div className={styles.filterSectionTitle}><IndianRupee size={12}/> Smart Actions</div>
            <button className={`${styles.smartToggleBtn} ${filters.sortHighSpend ? styles.active : ''}`} onClick={() => toggleSmartFilter('sortHighSpend')}>
              <TrendingUp size={14} strokeWidth={2.5}/> Top Spenders (Highest First)
            </button>
            <button className={`${styles.smartToggleBtn} ${filters.sortFrequentVisits ? styles.active : ''}`} onClick={() => toggleSmartFilter('sortFrequentVisits')}>
              <Activity size={14} strokeWidth={2.5}/> Frequent Visitors (Most Visits)
            </button>
            <button className={`${styles.smartToggleBtn} ${filters.filterLosingAudience ? styles.active : ''}`} onClick={() => toggleSmartFilter('filterLosingAudience')}>
              <UserMinus size={14} strokeWidth={2.5}/> Losing Audience ({'>'} 60 days)
            </button>
          </div>

          <div className={styles.filterSection}>
            <div className={styles.filterSectionTitle}><Gift size={12}/> Special Events</div>
            <select name="birthdayMonth" className={styles.selectInput} value={filters.birthdayMonth} onChange={handleFilterChange}>
              <option value="All">Birthdays (Any Month)</option>
              <option value="January">January</option><option value="February">February</option>
              <option value="March">March</option><option value="April">April</option>
              <option value="May">May</option><option value="June">June</option>
              <option value="July">July</option><option value="August">August</option>
              <option value="September">September</option><option value="October">October</option>
              <option value="November">November</option><option value="December">December</option>
            </select>
            <select name="anniversaryMonth" className={styles.selectInput} value={filters.anniversaryMonth} onChange={handleFilterChange}>
              <option value="All">Anniversaries (Any Month)</option>
              <option value="January">January</option><option value="February">February</option>
              <option value="March">March</option><option value="April">April</option>
              <option value="May">May</option><option value="June">June</option>
              <option value="July">July</option><option value="August">August</option>
              <option value="September">September</option><option value="October">October</option>
              <option value="November">November</option><option value="December">December</option>
            </select>
          </div>

          <div className={styles.filterSection} style={{ borderBottom: 'none' }}>
            <div className={styles.filterSectionTitle}><Users2 size={12}/> Account Status</div>
            <label className={styles.radioLabel} style={{marginBottom: '10px'}}>
                <input type="checkbox" name="hasDueAmount" checked={filters.hasDueAmount} onChange={handleFilterChange} /> 
                Show Only Pending Dues
            </label>
            <label className={styles.radioLabel}>
                <input type="checkbox" name="hasWalletBalance" checked={filters.hasWalletBalance} onChange={handleFilterChange} /> 
                Show Only Active Wallets
            </label>
          </div>
        </aside>

        <main className={styles.resultsPanel}>
          <div className={styles.resultStatsRow}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><Users size={16} strokeWidth={2.5}/></div>
              <div className={styles.statInfo}><h4>{filteredCustomers.length}</h4><p>Matched Users</p></div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><IndianRupee size={16} strokeWidth={2.5}/></div>
              <div className={styles.statInfo}><h4>₹{totalAudienceSpend.toLocaleString()}</h4><p>Total Revenue</p></div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}><Calendar size={16} strokeWidth={2.5}/></div>
              <div className={styles.statInfo}>
                <h4>{filters.sortHighSpend ? 'Top Spenders' : filters.sortFrequentVisits ? 'Most Visited' : filters.filterLosingAudience ? 'Inactive' : 'All Segment'}</h4>
                <p>Current View</p>
              </div>
            </div>
          </div>

          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <h3>Audience List</h3>
              <div className={styles.searchBox}>
                <Search size={12} className={styles.searchIcon} />
                <input type="text" placeholder="Search by name or phone..." value={searchQuery} onChange={(e) => {setSearchQuery(e.target.value); setCurrentPage(1);}} />
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className={styles.selectionBar}>
                <span>{selectedIds.length} users selected manually.</span>
                <button className={styles.clearBtn} onClick={() => setSelectedIds([])}>Clear Selection</button>
              </div>
            )}
            
            <div className={styles.tableWrapper}>
              <table className={styles.customTable}>
                <thead>
                  <tr>
                    <th style={{width: '30px'}}>
                      <input 
                        type="checkbox" 
                        className={styles.checkbox} 
                        checked={filteredCustomers.length > 0 && selectedIds.length === filteredCustomers.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Customer Details</th>
                    <th>Total Spend</th>
                    <th>Total Visits</th>
                    <th>Wallet Balance</th>
                    <th>Pending Due</th>
                    <th>Events</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" style={{textAlign: 'center', padding: '40px 0'}}>
                        <Loader2 size={24} style={{animation: 'spin 1s linear infinite', color: '#673de6', margin: '0 auto'}} />
                      </td>
                    </tr>
                  ) : paginatedCustomers.length > 0 ? (
                    paginatedCustomers.map(user => {
                      let visitDays = 999;
                      if (user.lastVisit) {
                          const diff = new Date() - new Date(user.lastVisit);
                          visitDays = Math.floor(diff / (1000 * 60 * 60 * 24));
                      }
                      
                      return (
                      <tr key={user.id}>
                        <td>
                          <input 
                            type="checkbox" 
                            className={styles.checkbox} 
                            checked={selectedIds.includes(user.id)}
                            onChange={() => handleSelectRow(user.id)}
                          />
                        </td>
                        <td>
                          <div className={styles.userProfile}>
                            <div className={styles.avatar}>{user.name.charAt(0)}</div>
                            <div className={styles.userInfo}>
                              <h4>{user.name}</h4>
                              <p>{user.phone} • {user.gender}</p>
                            </div>
                          </div>
                        </td>
                        
                        <td>
                          <div className={styles.moneyText}>₹{(user.totalSpent || 0).toLocaleString()}</div>
                        </td>

                        <td>
                          <span className={styles.visitBadge}>{user.totalVisits || 0} Visits</span>
                          {user.lastVisit && <span className={styles.visitSubText}>({visitDays} days ago)</span>}
                        </td>

                        <td>
                          <div style={{fontSize:'13px', fontWeight:600, color: user.walletBalance > 0 ? '#166534' : '#64748b'}}>
                              <Wallet size={12} style={{marginRight: '4px', verticalAlign: 'middle'}}/>
                              ₹{(user.walletBalance || 0).toLocaleString()}
                          </div>
                        </td>

                        <td>
                          <div style={{fontSize:'13px', fontWeight:600, color: user.dueAmount > 0 ? '#dc2626' : '#64748b'}}>
                              <AlertCircle size={12} style={{marginRight: '4px', verticalAlign: 'middle'}}/>
                              ₹{(user.dueAmount || 0).toLocaleString()}
                          </div>
                        </td>

                        <td>
                          <div style={{display:'flex', flexDirection:'column', gap:'4px', alignItems:'flex-start'}}>
                            {user.dob && <span className={`${styles.microBadge} ${styles.badgeEvent}`}><Gift size={10}/> {getMonthName(user.dob)}</span>}
                            {user.anniversary && <span className={`${styles.microBadge} ${styles.badgeEvent} ${styles.anniv}`}><Heart size={10}/> {getMonthName(user.anniversary)}</span>}
                            {!user.dob && !user.anniversary && <span style={{fontSize:'10px', color:'#94a3b8'}}>-</span>}
                          </div>
                        </td>
                      </tr>
                    )})
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className={styles.emptyState}>
                          <UserSearch size={32} color="#cbd5e1" style={{marginBottom:'8px'}}/>
                          <h3>No Customers Found</h3>
                          <p>Try adjusting your filters or search query.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredCustomers.length > 0 && !isLoading && (
              <div className={styles.paginationRow}>
                <div>
                  Rows per page: 
                  <select 
                    className={styles.rowSelect} 
                    value={rowsPerPage} 
                    onChange={(e) => {setRowsPerPage(Number(e.target.value)); setCurrentPage(1);}}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <div className={styles.pageControls}>
                  <span>{(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, filteredCustomers.length)} of {filteredCustomers.length}</span>
                  <button className={styles.pageBtn} disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={14}/></button>
                  <button className={styles.pageBtn} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={14}/></button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Audience;